import { I18n } from '@lingui/core'
import { withI18n } from '@lingui/react'
import {
  type NowMerchantExtended,
  type NowSellerDataForCart,
  type NowSlotExtended,
} from '@redteclab/api/clients/bully'
import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { compose } from 'redux'

import 'views/components/organisms/NowShippingModal/NowShippingModal.scss'
import {
  PAGE_TYPE_CATEGORY,
  PAGE_TYPE_SEARCH_RESULTS,
  TENANT_AT,
} from 'shared/consts'
import {
  NOW_CATEGORIES_SHIPPING_MODAL_SUBMIT,
  NOW_SERP_SHIPPING_MODAL_SUBMIT,
  type TrackingEventName,
} from 'shared/experiments/consts'
import trackOptimizelyEvent from 'shared/experiments/utils/trackOptimizelyEvent'
import { updateNowDataSuccessAction } from 'shared/store/ducks/now/actions'
import { type NowDataStateSlot } from 'types/redux'
import NowZipForm, {
  type INowDeliveryBoxData,
} from 'views/components/organisms/NowShippingModal/NowShippingModalContent/NowZipForm'
import { trackEligibleProduct } from 'views/components/organisms/ProductNowShippingModal'
import {
  MINIMUM_VALUE_NOT_REACHED,
  PRODUCTS_NOT_AVAILABLE,
} from 'views/consts/notEligibleReasons'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import buildClientAPI from 'views/providers/clientAPI'

export const validateZipCode = (
  zipCode: string,
  i18n: I18n,
  setErrorMessage: Dispatch<SetStateAction<string>>,
  tenant: string
) => {
  const zipCodeLength = tenant === TENANT_AT ? 4 : 5
  setErrorMessage('')

  if (zipCode === '') {
    return true
  }

  if (zipCode.length < zipCodeLength) {
    setErrorMessage(
      i18n._('form.error.input.minLength', { minLength: zipCodeLength })
    )
    return false
  }

  if (zipCode.length > zipCodeLength) {
    setErrorMessage(
      i18n._('form.error.input.maxLength', { maxLength: zipCodeLength })
    )
    return false
  }

  if (!Number(zipCode)) {
    setErrorMessage(i18n._('address.error.zip.invalid'))
    return false
  }
  return true
}

type NowShippingModalContentProps = {
  i18n: I18n
  productUpid: string
}

const NowShippingModalContent: FC<NowShippingModalContentProps> = ({
  productUpid,
  i18n,
}) => {
  const [notEligibleReasonCode, setNotEligibleReasonCode] = useState<
    number | undefined | null
  >(0)
  const localStorageData = localStorage.getItem('nowData')
  const nowDataState = localStorageData ? JSON.parse(localStorageData) : null
  const [zipCode, setZipCode] = useState(nowDataState?.zipCode)
  const [errorMessage, setErrorMessage] = useState('')
  const {
    publicRuntimeConfig: {
      publicConfig: { tenant },
      pageProperties: { pageType },
    },
  } = useShopAndRuntimeConfig()

  const [nowEligible, setNowEligible] = useState(false)
  const [nowDeliveryBoxData, setNowDeliveryBoxData] =
    useState<INowDeliveryBoxData | null>(null)

  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)

  const fetchNowData: (zipCodeToUse: string) => Promise<void> = useCallback(
    async zipCodeToUse => {
      try {
        if (zipCodeToUse && productUpid) {
          const { nowService } = buildClientAPI()

          const response: NowSellerDataForCart =
            await nowService.fetchBasicData({
              zipCode: zipCodeToUse,
              products: [
                {
                  upid: productUpid,
                  quantity: 1,
                  priceCentsGross: 1,
                  nowProduct: 0,
                },
              ],
            })

          let notEligibleReason: number | undefined | null =
            response.notEligibleReason

          if (notEligibleReason === MINIMUM_VALUE_NOT_REACHED) {
            notEligibleReason = undefined
          }

          const nowEligibleProduct: boolean =
            response.nowEligible ||
            response.notEligibleReason === MINIMUM_VALUE_NOT_REACHED
          setNowEligible(nowEligibleProduct)
          setNotEligibleReasonCode(notEligibleReason)

          trackOptimizelyEvent(trackEligibleProduct(nowEligibleProduct))

          let slots: NowSlotExtended[] = []
          if (response?.slots?.length) {
            const slot = response.slots[0]
            slots = response.slots.filter(
              item => item.deliveryDay === slot.deliveryDay
            )
          }

          setNowDeliveryBoxData({
            slots: slots as NowDataStateSlot[],
            merchant: { ...response.merchant } as NowMerchantExtended,
            centsDifferenceMinimumOrderValue:
              response.centsDifferenceMinimumOrderValue,
          })
          dispatch(
            updateNowDataSuccessAction({
              ...response,
              zipCode: zipCodeToUse,
            })
          )
          setZipCode(zipCodeToUse)
        } else {
          setNotEligibleReasonCode(undefined)
          setNowEligible(false)
          dispatch(updateNowDataSuccessAction({}))
          setZipCode('')
        }

        setIsLoading(false)
      } catch (error) {
        setNotEligibleReasonCode(PRODUCTS_NOT_AVAILABLE)
        setNowEligible(false)

        dispatch(
          updateNowDataSuccessAction({
            zipCode: zipCodeToUse,
            nowEligible: false,
            notEligibleReason: PRODUCTS_NOT_AVAILABLE,
          })
        )

        setIsLoading(false)
      }
    },
    [dispatch, productUpid]
  )

  const handleSubmit = useCallback(
    async zipToUse => {
      if (!zipToUse) {
        return
      }

      if (validateZipCode(zipToUse, i18n, setErrorMessage, tenant)) {
        if (zipToUse) {
          setIsLoading(true)
        }

        try {
          await fetchNowData(zipToUse)
        } catch (e) {
          setIsLoading(false)

          setNotEligibleReasonCode(PRODUCTS_NOT_AVAILABLE)
          setNowEligible(false)
        }

        const EVENT_MAP = {
          [PAGE_TYPE_SEARCH_RESULTS]: NOW_SERP_SHIPPING_MODAL_SUBMIT,
          [PAGE_TYPE_CATEGORY]: NOW_CATEGORIES_SHIPPING_MODAL_SUBMIT,
        }
        const event: TrackingEventName =
          EVENT_MAP[pageType as keyof typeof EVENT_MAP]

        trackOptimizelyEvent(event)
      }
    },
    [fetchNowData, i18n, pageType, tenant]
  )

  useEffect(() => {
    setIsLoading(false)

    if (!zipCode) {
      setNotEligibleReasonCode(undefined)
      setNowEligible(false)
      setErrorMessage('')
      dispatch(updateNowDataSuccessAction({}))
    }
  }, [dispatch, setNowEligible, zipCode])

  useEffect(() => {
    const data = localStorage.getItem('nowData')
    const nowData = data ? JSON.parse(data) : null

    setZipCode(nowData?.zipCode || '')
    void handleSubmit(nowData?.zipCode)
  }, [handleSubmit, nowDataState?.zipCode])

  return (
    <NowZipForm
      nowEligible={nowEligible}
      zipCode={zipCode}
      setZipCode={setZipCode}
      setErrorMessage={setErrorMessage}
      handleSubmit={handleSubmit}
      isLoading={isLoading}
      nowDeliveryBoxData={nowDeliveryBoxData}
      notEligibleReasonCode={notEligibleReasonCode}
      errorMessage={errorMessage}
    />
  )
}

export default compose(
  withShopAndRuntimeConfig,
  withI18n()
)(NowShippingModalContent)
